import React, { useState, useEffect } from 'react';
import { Typography, Button, Tooltip, Badge } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { 
  FacebookOutlined, 
  InstagramOutlined, 
  WhatsAppOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  AppstoreOutlined,
  HeartOutlined
} from '@ant-design/icons';
import { FaTiktok, FaUsers, FaTag, FaStar, FaMapMarkerAlt } from 'react-icons/fa';
import { fetchCatalogoInfo, formatImageUrl } from '../../services/api';
import './Footer.css';
import 'react-lazy-load-image-component/src/effects/blur.css';

const { Title, Text } = Typography;

const Footer = () => {
  // Estado para efectos hover y animaciones
  const [activeSection, setActiveSection] = useState(null);
  const [hoverGroup, setHoverGroup] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  
  // Estado para la información del catálogo
  const [catalogoInfo, setCatalogoInfo] = useState({
    telefono: '+591 74842996',
    email: 'info@channels.com',
    direccion: 'Calle Omiste entre Sucre, frente a Impuestos Nacionales',
    horarios_semana: 'Lun-Sáb: 8:00-20:00',
    horarios_domingo: 'Dom: 9:00-14:00',
    facebook_url: 'https://www.facebook.com/share/1BJs4XPzVu/',
    instagram_url: 'https://www.instagram.com',
    tiktok_url: 'https://www.tiktok.com/@accesorioschannels',
    whatsapp_url: 'https://wa.me/59174842996',
    grupos_whatsapp: [
      { name: "Grupo 1", link: "https://chat.whatsapp.com/LqwALOUhgol9k9uFSgkijy", members: "250+", status: "activo" },
      { name: "Grupo 2", link: "https://chat.whatsapp.com/D8Z4wfqn730A2MXEqxLIT3", members: "230+", status: "activo" },
      { name: "Grupo 3", link: "https://chat.whatsapp.com/CToEbetZpr0KCdffPK3W4A", members: "200+", status: "activo" },
      { name: "Grupo 4", link: "https://chat.whatsapp.com/C9bLWNAGXwYIO82AtOG98E", members: "245+", status: "activo" },
      { name: "Grupo 5", link: "https://chat.whatsapp.com/Gqn3BZSy9a5JQpBZBH2G53", members: "210+", status: "nuevo" },
      { name: "Grupo 6", link: "https://chat.whatsapp.com/BUQSFdzGQQ4FS0Abp4HjJs", members: "190+", status: "activo" }
    ],
    descripcion_empresa: 'Bisutería Channels - Los mejores accesorios para ti',
    logo_url: null,
    banner_url: null
  });
  
  // Efecto para cargar información del catálogo
  useEffect(() => {
    const cargarInfoCatalogo = async () => {
      try {
        const info = await fetchCatalogoInfo();
        setCatalogoInfo(info);
      } catch (error) {
        console.error('Error al cargar información del catálogo:', error);
        // Mantener valores por defecto si hay error
      }
    };
    
    cargarInfoCatalogo();
  }, []);

  // Efecto para mostrar feedback al cargar
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFeedback(true);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  
  const feedbackVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 10 },
    visible: {
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3
      }
    }
  };

  // Abrir enlaces externos
  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  // Usar los grupos de WhatsApp de la información del catálogo
  const whatsappGroups = catalogoInfo.grupos_whatsapp || [];

  // Secciones disponibles del sitio
  const sections = [
    { name: "Catálogo", link: "/catalogo", icon: <AppstoreOutlined /> },
    { name: "Inicio", link: "/", icon: <AppstoreOutlined /> },
    { name: "Promociones", link: "/promociones", icon: <FaTag /> }
  ];

  return (
    <footer className="site-footer">
      {/* Wave divider */}
      <div className="wave-divider">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" />
        </svg>
      </div>
      
      {/* Particles background effect */}
      <div className="particles-bg"></div>
      
      <div className="footer-content">
        <motion.div 
          className="footer-main"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          {/* Sección de contacto */}
          <motion.div 
            className="footer-section" 
            variants={itemVariants}
            onMouseEnter={() => setActiveSection('contact')}
            onMouseLeave={() => setActiveSection(null)}
          >
            <Title level={5} className="footer-title">
              <FaMapMarkerAlt style={{ marginRight: '8px', fontSize: '16px' }} /> Contacto
            </Title>
            <ul className="info-list">
              <motion.li 
                className="info-item"
                whileHover={{ x: 5 }}
                onClick={() => openExternalLink(catalogoInfo.whatsapp_url || "https://wa.me/59174842996")}
                style={{ cursor: 'pointer' }}
              >
                <PhoneOutlined className="info-icon" />
                <Text className="info-text">{catalogoInfo.telefono}</Text>
              </motion.li>
              
              <motion.li 
                className="info-item"
                whileHover={{ x: 5 }}
                onClick={() => window.location.href = `mailto:${catalogoInfo.email}`}
                style={{ cursor: 'pointer' }}
              >
                <MailOutlined className="info-icon" />
                <Text className="info-text">{catalogoInfo.email}</Text>
              </motion.li>
              
              <motion.li 
                className="info-item"
                whileHover={{ x: 5 }}
              >
                <EnvironmentOutlined className="info-icon" />
                <Text className="info-text">{catalogoInfo.direccion}</Text>
              </motion.li>
              
              <motion.li 
                className="info-item"
                whileHover={{ x: 5 }}
              >
                <ClockCircleOutlined className="info-icon" />
                <Text className="info-text">{catalogoInfo.horarios_semana}, {catalogoInfo.horarios_domingo}</Text>
              </motion.li>
            </ul>
          </motion.div>

          {/* Sección de enlaces del sitio */}
          <motion.div 
            className="footer-section" 
            variants={itemVariants}
            onMouseEnter={() => setActiveSection('sections')}
            onMouseLeave={() => setActiveSection(null)}
          >
            <Title level={5} className="footer-title">
              <FaStar style={{ marginRight: '8px', fontSize: '16px' }} /> Secciones
            </Title>
            
            <ul className="sections-list">
              {sections.map((section, index) => (
                <motion.li 
                  className="link-item" 
                  key={index}
                  whileHover={{ x: 5 }}
                >
                  <a href={section.link} className="footer-link">
                    {section.name}
                  </a>
                </motion.li>
              ))}
            </ul>
            
            <Title level={5} className="footer-title" style={{ marginTop: '20px' }}>
              <HeartOutlined style={{ marginRight: '8px', fontSize: '16px' }} /> Síguenos
            </Title>
            
            <div className="social-container">
              <motion.div whileHover={{ scale: 1.15, rotate: 8 }} whileTap={{ scale: 0.9 }}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<FacebookOutlined />}
                  className="social-button facebook"
                  onClick={() => openExternalLink(catalogoInfo.facebook_url || "https://www.facebook.com/share/1BJs4XPzVu/")}
                />
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.15, rotate: 8 }} whileTap={{ scale: 0.9 }}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<InstagramOutlined />}
                  className="social-button instagram"
                  onClick={() => openExternalLink(catalogoInfo.instagram_url || "https://www.instagram.com")}
                />
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.15, rotate: 8 }} whileTap={{ scale: 0.9 }}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<FaTiktok />}
                  className="social-button tiktok"
                  onClick={() => openExternalLink(catalogoInfo.tiktok_url || "https://www.tiktok.com/@accesorioschannels")}
                />
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.15, rotate: 8 }} whileTap={{ scale: 0.9 }}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<WhatsAppOutlined />}
                  className="social-button whatsapp"
                  onClick={() => openExternalLink(catalogoInfo.whatsapp_url || "https://wa.me/59174842996")}
                />
              </motion.div>
            </div>
          </motion.div>

          {/* Sección de grupos de WhatsApp */}
          <motion.div 
            className="footer-section" 
            variants={itemVariants}
            onMouseEnter={() => setActiveSection('groups')}
            onMouseLeave={() => setActiveSection(null)}
          >
            {whatsappGroups.find(group => group.status === "nuevo") && (
              <span className="corner-ribbon">¡Nuevo!</span>
            )}
            
            <Title level={5} className="footer-title">
              <FaUsers style={{ marginRight: '8px', fontSize: '16px' }} /> Grupos de WhatsApp
            </Title>
            
            <Text className="info-text">
              Únete para recibir ofertas exclusivas y novedades
            </Text>
            
            <div className="whatsapp-groups">
              {whatsappGroups.map((group, index) => (
                <Tooltip 
                  key={index} 
                  title={`${group.members} miembros activos`}
                  color="#e91e63"
                >
                  <motion.button
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="group-button"
                    onClick={() => openExternalLink(group.link)}
                    onMouseEnter={() => setHoverGroup(index)}
                    onMouseLeave={() => setHoverGroup(null)}
                  >
                    <FaUsers />
                    <span>{group.name}</span>
                    <motion.span 
                      className="group-bubble"
                      initial={false}
                      animate={hoverGroup === index ? { scale: 1.2 } : { scale: 1 }}
                    >
                      {index + 1}
                    </motion.span>
                  </motion.button>
                </Tooltip>
              ))}
            </div>
            
            {/* Feedback notification */}
            <AnimatePresence>
              {showFeedback && (
                <motion.div
                  className="feedback-message"
                  variants={feedbackVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    marginTop: '20px',
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.5)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#9c27b0',
                    border: '1px dashed rgba(156, 39, 176, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <FaStar style={{ color: '#e91e63' }} />
                  <span>¡Los grupos se actualizan diariamente!</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
        
        <div className="copyright-divider" />
        
        <motion.div 
          className="copyright"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <span>© {new Date().getFullYear()} Bisutería Channels • Todos los derechos reservados</span>
          
          <motion.div 
            className="footer-logo"
            whileHover={{ scale: 1.1, rotate: [0, -3, 3, -3, 0] }}
            transition={{ duration: 0.5 }}
          >
            {catalogoInfo.logo_url ? (
              <LazyLoadImage
                alt="Logo Channels"
                height={40}
                src={formatImageUrl(catalogoInfo.logo_url)}
                effect="blur"
                placeholderSrc="/assets/logo-placeholder.png"
              />
            ) : (
              <LazyLoadImage
                alt="Logo Channels"
                height={40}
                src="/assets/logo-channels.png"
                effect="blur"
                placeholderSrc="/assets/logo-placeholder.png"
              />
            )}
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
